import React from "react";
import { useList } from "@/lib/hooks";
import { PageHeader, Panel, EmptyState, money } from "@/components/common";
import { ChartLineUp, ArrowSquareOut, Truck } from "@phosphor-icons/react";

const DAT_LINKS = [
  ["Van", "https://www.dat.com/industry-trends/trendlines/van/national-rates"],
  ["Reefer", "https://www.dat.com/industry-trends/trendlines/reefer/national-rates"],
  ["Flatbed", "https://www.dat.com/industry-trends/trendlines/flatbed/national-rates"],
];

function Bars({ rows, testid }) {
  const max = Math.max(...rows.map((r) => r.avg_rpm), 0.01);
  return (
    <ul className="space-y-3" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.key}>
          <div className="flex justify-between text-sm"><span className="font-medium text-slate-800">{r.key}</span><span className="font-mono font-bold text-slate-900">${r.avg_rpm}/mi</span></div>
          <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-600 transition-[width] duration-700" style={{ width: `${(r.avg_rpm / max) * 100}%` }} /></div>
          <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{r.loads} loads · avg {money(r.avg_rate)} · {r.miles.toLocaleString()} mi</p>
        </li>
      ))}
    </ul>
  );
}

export default function AsterismAnalytics() {
  const { data, isLoading } = useList("rate-analytics", "/analytics/rates", { refetchInterval: 60000 });
  const weekly = data?.weekly || [];
  const wMax = Math.max(...weekly.map((w) => w.avg_rpm), 0.01);
  return (
    <div>
      <PageHeader title="Asterism Analytics" subtitle="Spot-rate intelligence by truck type and lane, from every load Asterism books. Refreshes every minute." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Panel className="p-4"><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Network Avg RPM</p><p className="font-heading font-extrabold text-3xl text-slate-900 mt-1" data-testid="an-overall">${data?.overall_rpm || 0}</p></Panel>
        <Panel className="p-4"><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Loads Analyzed</p><p className="font-heading font-extrabold text-3xl text-slate-900 mt-1" data-testid="an-sample">{data?.sample || 0}</p></Panel>
        <Panel className="p-4 lg:col-span-2"><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">Live National Spot Rates (DAT Trendlines)</p>
          <div className="flex flex-wrap gap-2">{DAT_LINKS.map(([l, u]) => <a key={l} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-300 text-sm text-slate-700 hover:border-blue-400 hover:text-blue-600" data-testid={`an-dat-${l}`}>{l}<ArrowSquareOut size={13} /></a>)}</div>
          <p className="text-[11px] text-slate-400 mt-2">Live DAT/Truckstop data can be plugged in here with an API key.</p></Panel>
      </div>
      {isLoading ? <p className="text-slate-400">Loading…</p> : !data?.sample ? <Panel><EmptyState icon={ChartLineUp} title="No Rate Data Yet" hint="Rates appear as loads with rate and miles are booked through Asterism AI." /></Panel> : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Panel className="p-5"><h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Truck size={18} />Avg RPM by Truck Type</h3><Bars rows={data.by_equipment} testid="an-equipment" /></Panel>
          <Panel className="p-5"><h3 className="font-heading font-bold text-slate-900 mb-4">Top Lanes (State → State)</h3>{data.top_lanes.length ? <Bars rows={data.top_lanes} testid="an-lanes" /> : <p className="text-sm text-slate-400">Lanes appear when addresses include a state.</p>}</Panel>
          <Panel className="p-5"><h3 className="font-heading font-bold text-slate-900 mb-4">Weekly RPM Trend</h3>
            <div className="flex items-end gap-2 h-48" data-testid="an-weekly">{weekly.map((w) => (
              <div key={w.key} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                <span className="text-[10px] font-mono text-slate-500">${w.avg_rpm}</span>
                <div className="w-full rounded-t-md bg-gradient-to-t from-blue-600 to-sky-400" style={{ height: `${(w.avg_rpm / wMax) * 80}%` }} />
                <span className="text-[9px] text-slate-400">{w.key.slice(5)}</span>
              </div>
            ))}</div></Panel>
        </div>
      )}
    </div>
  );
}
